/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { appPalette } from '@sidekick/design-tokens';
import { useMemo, useState } from 'react';
import { inputStyle, monoLabel } from '../../lib/ui.js';
import { trpc } from '../../lib/trpc.js';

const p = appPalette;

const MAX_COMMITMENTS = 3;

type EvalStatus = 'pending' | 'hit' | 'missed' | 'partial';

interface Slot {
  description: string;
  linkedActionId: string | null;
}

/**
 * Three daily commitments — the small set the user actually signs up for
 * after the brief lands. Two modes:
 *
 *   - Pinned: commitments exist; show them with a 3-way Hit/Partial/Missed
 *     status control. An Edit affordance drops back to pick mode.
 *   - Pick: no commitments yet (or the user clicked Edit); up to three
 *     slots seeded from carryover or the brief's suggested priorities.
 */
export function Commitments({
  briefDate,
  suggestedPriorities,
}: {
  briefDate: string;
  suggestedPriorities: ReadonlyArray<{ ordinal: number; description: string }>;
}) {
  const utils = trpc.useUtils();
  const todayQuery = trpc.commitments.today.useQuery({ briefDate });
  const [editing, setEditing] = useState(false);

  const committed = todayQuery.data?.committed ?? [];
  const carryover = todayQuery.data?.carryover ?? [];

  // Pinned whenever commitments exist and the user hasn't asked to edit.
  const pinned = committed.length > 0 && !editing;

  if (todayQuery.isPending) {
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ ...monoLabel }}>Today&apos;s commitments</div>
        <span style={{ ...monoLabel }}>…</span>
      </section>
    );
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {pinned ? (
        <PinnedMode
          committed={committed}
          onEdit={() => setEditing(true)}
          onEvaluated={async () => {
            await utils.commitments.today.invalidate({ briefDate });
          }}
        />
      ) : (
        <PickMode
          briefDate={briefDate}
          carryover={carryover}
          suggestedPriorities={suggestedPriorities}
          onCommitted={async () => {
            await utils.commitments.today.invalidate({ briefDate });
            setEditing(false);
          }}
        />
      )}
    </section>
  );
}

interface CommitmentView {
  id: string;
  description: string;
  kind: 'action_bound' | 'behavioral' | 'outcome';
  status: EvalStatus;
  linkedActionId: string | null;
  createdAt: string;
}

function PinnedMode({
  committed,
  onEdit,
  onEvaluated,
}: {
  committed: ReadonlyArray<CommitmentView>;
  onEdit: () => void;
  onEvaluated: () => Promise<void> | void;
}) {
  const evaluate = trpc.commitments.evaluate.useMutation({
    onSuccess: async () => {
      await onEvaluated();
    },
  });

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 10,
        }}
      >
        <span style={{ ...monoLabel }}>Today&apos;s commitments</span>
        <button
          type="button"
          onClick={onEdit}
          aria-label="Edit commitments"
          style={{
            background: 'transparent',
            border: 'none',
            color: p.muted,
            fontSize: 12,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            padding: '0 4px',
          }}
        >
          Edit
        </button>
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {committed.map((c) => (
          <CommitmentPinnedRow
            key={c.id}
            commitment={c}
            pending={evaluate.isPending}
            onStatus={(status) => evaluate.mutate({ commitmentId: c.id, status })}
          />
        ))}
      </ul>

      {evaluate.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{evaluate.error.message}</span>
      ) : null}
    </>
  );
}

const STATUS_OPTIONS: ReadonlyArray<{ status: Exclude<EvalStatus, 'pending'>; label: string }> = [
  { status: 'hit', label: 'Hit' },
  { status: 'partial', label: 'Partial' },
  { status: 'missed', label: 'Missed' },
];

function CommitmentPinnedRow({
  commitment,
  pending,
  onStatus,
}: {
  commitment: CommitmentView;
  pending: boolean;
  onStatus: (status: Exclude<EvalStatus, 'pending'>) => void;
}) {
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 12,
        padding: '12px 0',
        borderTop: `1px solid ${p.line}`,
        alignItems: 'center',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-serif)',
          fontFeatureSettings: 'var(--serif-feat)',
          fontSize: 15,
          color: p.fg,
        }}
      >
        {commitment.description}
      </span>
      <div style={{ display: 'flex', gap: 6 }} role="group" aria-label="Commitment status">
        {STATUS_OPTIONS.map((opt) => {
          const active = commitment.status === opt.status;
          return (
            <button
              key={opt.status}
              type="button"
              onClick={() => onStatus(opt.status)}
              disabled={pending}
              aria-label={opt.label}
              aria-pressed={active}
              style={{
                background: active ? p.fg : 'transparent',
                color: active ? p.bg : p.muted,
                border: `1px solid ${active ? p.fg : p.line}`,
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                padding: '4px 9px',
                cursor: pending ? 'wait' : 'pointer',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </li>
  );
}

function PickMode({
  briefDate,
  carryover,
  suggestedPriorities,
  onCommitted,
}: {
  briefDate: string;
  carryover: ReadonlyArray<{ description: string; linkedActionId: string | null }>;
  suggestedPriorities: ReadonlyArray<{ ordinal: number; description: string }>;
  onCommitted: () => Promise<void> | void;
}) {
  const actionsQuery = trpc.actions.list.useQuery();
  const set = trpc.commitments.set.useMutation({
    onSuccess: async () => {
      await onCommitted();
    },
  });

  const nextActions = useMemo(
    () => (actionsQuery.data ?? []).filter((a) => a.status === 'next'),
    [actionsQuery.data],
  );

  const [slots, setSlots] = useState<Slot[]>(() => seedSlots(carryover, suggestedPriorities));

  const updateSlot = (index: number, next: Slot) => {
    setSlots((prev) => prev.map((s, i) => (i === index ? next : s)));
  };

  const removeSlot = (index: number) => {
    setSlots((prev) => prev.filter((_, i) => i !== index));
  };

  const addSlot = () => {
    setSlots((prev) =>
      prev.length >= MAX_COMMITMENTS ? prev : [...prev, { description: '', linkedActionId: null }],
    );
  };

  const onCommit = () => {
    const commitments = slots
      .map((s) => ({ description: s.description.trim(), linkedActionId: s.linkedActionId }))
      .filter((s) => s.description !== '')
      .slice(0, MAX_COMMITMENTS);
    if (commitments.length === 0) return;
    set.mutate({ briefDate, commitments });
  };

  const canAdd = slots.length < MAX_COMMITMENTS;
  const hasContent = slots.some((s) => s.description.trim() !== '');

  return (
    <>
      <span style={{ ...monoLabel }}>Today&apos;s commitments</span>
      <p
        style={{
          fontFamily: 'var(--font-serif)',
          fontFeatureSettings: 'var(--serif-feat)',
          fontStyle: 'italic',
          fontSize: 15,
          color: p.muted,
          margin: 0,
        }}
      >
        Pick up to three things you&apos;re committing to today.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {slots.map((slot, i) => (
          <SlotRow
            key={i}
            index={i}
            slot={slot}
            nextActions={nextActions}
            onChange={(next) => updateSlot(i, next)}
            onRemove={() => removeSlot(i)}
          />
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 4 }}>
        <button
          type="button"
          onClick={addSlot}
          disabled={!canAdd}
          aria-label="Add commitment"
          style={{
            background: 'transparent',
            border: 'none',
            color: canAdd ? p.muted : p.line,
            fontSize: 12,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            cursor: canAdd ? 'pointer' : 'not-allowed',
            padding: '0 4px',
          }}
        >
          + Add
        </button>
        <button
          type="button"
          onClick={onCommit}
          disabled={set.isPending || !hasContent}
          style={{
            padding: '10px 18px',
            background: p.fg,
            color: p.bg,
            border: 'none',
            fontSize: 13,
            cursor: set.isPending || !hasContent ? 'not-allowed' : 'pointer',
            opacity: set.isPending || !hasContent ? 0.6 : 1,
          }}
        >
          {set.isPending ? 'Committing…' : 'Commit to these'}
        </button>
      </div>

      {set.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{set.error.message}</span>
      ) : null}
    </>
  );
}

function SlotRow({
  index,
  slot,
  nextActions,
  onChange,
  onRemove,
}: {
  index: number;
  slot: Slot;
  nextActions: ReadonlyArray<{ id: string; description: string }>;
  onChange: (next: Slot) => void;
  onRemove: () => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: 8,
        alignItems: 'center',
      }}
    >
      <input
        type="text"
        value={slot.description}
        onChange={(e) =>
          // Free-typing breaks the link to a next action — it's now bespoke.
          onChange({ description: e.target.value, linkedActionId: null })
        }
        placeholder={`Commitment ${index + 1}`}
        aria-label={`Commitment ${index + 1}`}
        style={{ ...inputStyle, width: '100%' }}
      />
      <select
        value={slot.linkedActionId ?? ''}
        onChange={(e) => {
          const id = e.target.value;
          if (id === '') return;
          const action = nextActions.find((a) => a.id === id);
          if (!action) return;
          onChange({ description: action.description, linkedActionId: action.id });
        }}
        aria-label={`Commitment ${index + 1} from a next action`}
        style={{
          ...inputStyle,
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          letterSpacing: '0.06em',
          padding: '10px 8px',
          maxWidth: 170,
        }}
      >
        <option value="">from a next action…</option>
        {nextActions.map((a) => (
          <option key={a.id} value={a.id}>
            {a.description}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove commitment ${index + 1}`}
        title="Remove"
        style={{
          background: 'transparent',
          border: 'none',
          color: p.muted,
          cursor: 'pointer',
          fontSize: 16,
          padding: '0 4px',
        }}
      >
        ×
      </button>
    </div>
  );
}

/**
 * Seed pick-mode slots: prefer staged carryover from last night's
 * shutdown; otherwise take the first three suggested priorities (text
 * only — they aren't bound to an action). Always at least one slot.
 */
function seedSlots(
  carryover: ReadonlyArray<{ description: string; linkedActionId: string | null }>,
  suggestedPriorities: ReadonlyArray<{ ordinal: number; description: string }>,
): Slot[] {
  if (carryover.length > 0) {
    return carryover
      .slice(0, MAX_COMMITMENTS)
      .map((c) => ({ description: c.description, linkedActionId: c.linkedActionId }));
  }
  if (suggestedPriorities.length > 0) {
    return suggestedPriorities
      .slice(0, MAX_COMMITMENTS)
      .map((sp) => ({ description: sp.description, linkedActionId: null }));
  }
  return [{ description: '', linkedActionId: null }];
}
